import { FC, useEffect } from 'react';
import styles from './Autocomplete.module.css';
import { Controller, get, useFormContext } from 'react-hook-form';
import { Autocomplete, TextField as MuiTextField, Stack, Typography } from '@mui/material';
import AutoCompleteProps from '../../../../../@types/AutoComplete';

export const AutoComplete: FC<AutoCompleteProps> = ({ name, label, options, allowUserInput = false }) => {
  const { control, formState: { errors } } = useFormContext();
  const error = get(errors, name);
  return (
    <Controller
      name={name}
      defaultValue={''}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          options={options}
          isOptionEqualToValue={(option, value) => true}
          value={value}
          onChange={(event, values) => {
            onChange(values?.value)
          }}
          renderInput={(params) => {
            return (
              <Stack direction="column" spacing={0.5}>
                {label &&
                  <Typography variant="secondaryFont">
                    {label}
                  </Typography>
                }
                <MuiTextField className={styles.TextField} {...params} onChange={allowUserInput ? onChange : () => { }} fullWidth variant="outlined" />
                <Typography variant="errorFont">
                  {String(error?.message ?? "")}
                </Typography>
              </Stack>
            )
          }}
        />
      )
      }
    />
  );
};


export const StateFieldAutoComplete: FC<AutoCompleteProps> = ({ name, label, options, allowUserInput = false, hidden = false }) => {
  const { control, formState: { errors }, setValue, unregister, watch, resetField, getValues } = useFormContext();
  let parentRoot = name.split('.');
  parentRoot.pop();
  const countryCode = watch(`${parentRoot.join('.')}.countryCode`);
  const error = get(errors, name);

  useEffect(() => {
    if (countryCode === 'US' || countryCode === 'CA') {
      const currentValue = getValues(`${name}`);
      const foundIndex = options.findIndex((option) => {
        if (Array.isArray(option.value)) {
          return option.value[0] === currentValue;
        } else {
          return option.value === currentValue
        }
      });
      if (foundIndex === -1) {
        resetField(`${name}`);
        resetField(`${name}Code`);
      }
    }

  }, [countryCode]);

  return (
    <Controller
      name={name}
      defaultValue={''}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          freeSolo={allowUserInput}
          autoHighlight={true}
          autoSelect={countryCode === undefined || countryCode === 'US' || countryCode === 'CA'}
          options={options}
          hidden={hidden}
          isOptionEqualToValue={(option, value) => true}
          value={Array.isArray(value) ? value[0] : value}
          onChange={(event, value, reason) => {
            
            if (Array.isArray(value?.value) && reason !== 'clear') {
              onChange(value.value[0]);
              setValue(`${name}Code`, value.value[1]);
            } else {
              onChange(value?.value);
              unregister(`${name}Code`);
            }
          }}
          renderInput={(params) => {
            return (
              <Stack direction="column" spacing={0.5}>
                {label &&
                  <Typography variant="secondaryFont">
                    {label}
                  </Typography>
                }
                <MuiTextField
                  sx={{ minWidth: '250px' }}
                  className={styles.TextField}
                  {...params}
                  onChange={allowUserInput ? onChange : (value) => { unregister(`${name}Code`) }}
                  fullWidth
                  variant="outlined" />
                <Typography variant="errorFont">
                  {String(error?.message ?? "")}
                </Typography>
              </Stack>
            )
          }}
        />
      )
      }
    />
  )
}

export const CountryFieldAutoComplete: FC<AutoCompleteProps> = ({ name, label, options, allowUserInput = false }) => {
  const { control, formState: { errors }, setValue, unregister } = useFormContext();
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      defaultValue={''}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          freeSolo={allowUserInput}
          autoHighlight={true}
          autoSelect={true}
          options={options}
          isOptionEqualToValue={(option, value) => true}
          value={Array.isArray(value) ? value[0] : value}
          onChange={(event, values, reason) => {
            if (Array.isArray(values?.value)) {
              onChange(values.value[0]);
              setValue(`${name}Code`, values.value[1]);
            } else {
              onChange(values?.value);
              unregister(`${name}Code`);
            }
          }}
          renderInput={(params) => {
            return (
              <Stack direction="column" spacing={0.5}>
                {label &&
                  <Typography variant="secondaryFont">
                    {label}
                  </Typography>
                }
                <MuiTextField
                  sx={{ minWidth: '250px' }}
                  className={styles.TextField}
                  {...params}
                  onChange={allowUserInput ? onChange : (value) => { unregister(`${name}Code`) }}
                  fullWidth
                  variant="outlined" />
                <Typography variant="errorFont">
                  {String(error?.message ?? "")}
                </Typography>
              </Stack>
            )
          }}
        />
      )
      }
    />
  )
}
